import React, { useState, useEffect, useMemo } from 'react'
import { useDraft } from '../useDraft'
import DraftPrompt from '../components/DraftPrompt'
import { useLanguage } from '../LanguageContext'

const GEO_URL = 'https://raw.githubusercontent.com/russ666/all-countries-and-cities-json/master/countries.json'
let geoCache = null

function useGeoData() {
  const [data,    setData]    = useState(geoCache || null)
  const [loading, setLoading] = useState(!geoCache)

  useEffect(() => {
    if (geoCache) { setLoading(false); return }
    fetch(GEO_URL)
      .then(r => r.json())
      .then(json => { geoCache = json; setData(json); setLoading(false) })
      .catch(() => { geoCache = {}; setData({}); setLoading(false) })
  }, [])

  return { data, loading }
}

function useCountries(geoData) {
  return useMemo(() => {
    if (!geoData) return []
    return Object.keys(geoData).sort((a, b) => a.localeCompare(b))
  }, [geoData])
}

function useCities(geoData, countryName) {
  return useMemo(() => {
    if (!geoData || !countryName) return []
    const list = geoData[countryName]
    if (!Array.isArray(list)) return []
    return [...list].sort((a, b) => a.localeCompare(b))
  }, [geoData, countryName])
}

function SearchableSelect({ id, label, placeholder, options, value, onChange, disabled, loading }) {
  const { t } = useLanguage()
  const [query, setQuery] = useState(value || '')
  const [open,  setOpen]  = useState(false)

  useEffect(() => { if (!value) setQuery('') }, [value])
  useEffect(() => { if (value && value !== query) setQuery(value) }, [value])

  const filtered = useMemo(() => {
    if (!query || query.length < 1) return options.slice(0, 100)
    const q = query.toLowerCase()
    return options.filter(o => o.toLowerCase().includes(q)).slice(0, 80)
  }, [options, query])

  const select = (option) => { setQuery(option); onChange(option); setOpen(false) }

  return (
    <div className="svc-field">
      <label className="svc-label" htmlFor={id}>{label}</label>
      <div className="svc-airport-wrap">
        <input
          id={id} type="text"
          className={`svc-input${disabled ? ' svc-input--disabled' : ''}`}
          value={query}
          placeholder={loading ? t('loading') : placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={e => { setQuery(e.target.value); onChange(''); setOpen(true) }}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
        />
        {open && loading && <ul className="svc-airport-dropdown"><li className="svc-airport-option svc-airport-option--muted">{t('loading')}</li></ul>}
        {open && !loading && filtered.length > 0 && (
          <ul className="svc-airport-dropdown">
            {filtered.map(option => (
              <li key={option}>
                <button type="button" className="svc-airport-option svc-airport-option--geo" onMouseDown={() => select(option)}>{option}</button>
              </li>
            ))}
          </ul>
        )}
        {open && !loading && query.length >= 1 && filtered.length === 0 && (
          <ul className="svc-airport-dropdown"><li className="svc-airport-option svc-airport-option--muted">{t('airportNoResults', { query })}</li></ul>
        )}
      </div>
    </div>
  )
}

const ROOM_PREF_IDS = ['suite', 'connect', 'highfloor', 'privacy']
const ROOM_LABEL_KEY = {
  suite: 'roomSuite',
  connect: 'roomConnecting',
  highfloor: 'roomHighFloor',
  privacy: 'roomPrivacy',
}

function ConfirmRow({ label, value }) {
  return (
    <div className="svc-confirm__row">
      <span className="svc-confirm__key">{label}</span>
      <span className="svc-confirm__val">{value}</span>
    </div>
  )
}

const EMPTY_STATE = {
  contactEmail: '',
  country:     '',
  city:        '',
  checkIn:     '',
  checkOut:    '',
  guests:      1,
  roomPrefs:   [],
  specialReqs: '',
}

export default function PartnerHotels({ onBack, member }) {
  const { t } = useLanguage()
  const { data: geoData, loading: geoLoading } = useGeoData()
  const countries = useCountries(geoData)

  const [submitted,   setSubmitted]   = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const [formState, setFormState] = useState({
    ...EMPTY_STATE,
    contactEmail: member?.email || '',
  })
  const { contactEmail, country, city, checkIn, checkOut, guests, roomPrefs, specialReqs } = formState

  const draft = useDraft('partner_hotels', formState, setFormState, EMPTY_STATE)

  // After draft restore, if contactEmail ended up empty, fall back to member email
  useEffect(() => {
    if (!formState.contactEmail && member?.email) {
      setFormState(prev => ({ ...prev, contactEmail: member.email }))
    }
  }, [draft.draftLoading])

  const cities = useCities(geoData, country)

  const set = (key) => (val) => {
    setFormState(prev => {
      const next = { ...prev, [key]: val }
      draft.saveProgress(next)
      return next
    })
  }

  const handleCountryChange = (val) => {
    setFormState(prev => {
      const next = { ...prev, country: val, city: '' }
      draft.saveProgress(next)
      return next
    })
  }

  const togglePref = (id) => {
    setFormState(prev => {
      const next = {
        ...prev,
        roomPrefs: prev.roomPrefs.includes(id)
          ? prev.roomPrefs.filter(p => p !== id)
          : [...prev.roomPrefs, id],
      }
      draft.saveProgress(next)
      return next
    })
  }

  const nights = () => {
    if (!checkIn || !checkOut) return null
    const diff = (new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)
    return diff > 0 ? diff : null
  }

  const handleSubmit  = (e) => { e.preventDefault(); setShowConfirm(true) }

  const handleConfirm = async () => {
    setSubmitError('')
    try {
      await draft.submitForm(formState)
      setShowConfirm(false)
      setSubmitted(true)
    } catch (_) {
      setSubmitError(t('failedSubmit'))
    }
  }

  const n = nights()
  const durationStr = n ? (n === 1 ? t('nightsSingular', { n: String(n) }) : t('nightsPlural', { n: String(n) })) : ''

  if (draft.draftLoading) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted" style={{ opacity: 0.5 }}>
          <div className="svc-submitted__icon">◌</div>
          <p className="svc-submitted__body">{t('loading')}</p>
        </div>
      </div>
    )
  }

  if (draft.draftDecision === 'pending') {
    return (
      <div className="db-page svc-page">
        <div className="svc-header">
          <button className="svc-back" onClick={onBack} aria-label={t('back')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {t('dashboard')}
          </button>
          <div className="svc-header__title-wrap">
            <p className="svc-header__overline">{t('serviceRequest')}</p>
            <h1 className="svc-header__title">{t('partnerHotelsTitle')}</h1>
          </div>
        </div>
        <DraftPrompt draft={draft.existingDraft} serviceName={t('partnerHotelsTitle')} onContinue={draft.chooseContinue} onStartNew={draft.chooseNew} />
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted">
          <div className="svc-submitted__icon" aria-hidden="true">✦</div>
          <h2 className="svc-submitted__heading">{t('requestReceived')}</h2>
          <p className="svc-submitted__body">{t('requestReceivedBody')}</p>
          <p className="svc-submitted__sig">{t('signatureMwuilu')}</p>
          <button className="svc-btn-ghost" onClick={onBack}>{t('returnToDashboard')}</button>
        </div>
      </div>
    )
  }

  if (showConfirm) {
    return (
      <div className="db-page svc-page">
        <div className="svc-confirm">
          <p className="svc-confirm__overline">{t('pleaseReview')}</p>
          <h2 className="svc-confirm__heading">{t('partnerHotelsTitle')}</h2>
          <div className="svc-confirm__rows">
            <ConfirmRow label={t('contactEmail')}    value={contactEmail || '—'} />
            <ConfirmRow label={t('country')}         value={country  || '—'} />
            <ConfirmRow label={t('city')}            value={city     || '—'} />
            <ConfirmRow label={t('checkIn')}         value={checkIn  || '—'} />
            <ConfirmRow label={t('checkOut')}        value={checkOut || '—'} />
            {n && <ConfirmRow label={t('duration')} value={durationStr} />}
            <ConfirmRow label={t('guests')} value={`${guests} ${guests !== 1 ? t('guestsPlural') : t('guestsSingular')}`} />
            {roomPrefs.length > 0 && (
              <ConfirmRow label={t('roomPreferences')} value={roomPrefs.map(id => t(ROOM_LABEL_KEY[id])).join(', ')} />
            )}
            {specialReqs && <ConfirmRow label={t('specialRequirements')} value={specialReqs} />}
          </div>
          {submitError && <p className="mw-error" style={{ marginBottom: 12 }}>{submitError}</p>}
          <div className="svc-confirm__actions">
            <button type="button" className="svc-btn-ghost"   onClick={() => setShowConfirm(false)}>{t('edit')}</button>
            <button type="button" className="svc-btn-primary" onClick={handleConfirm}>{t('confirmSubmit')}</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="db-page svc-page">
      <div className="svc-header">
        <button className="svc-back" onClick={onBack} aria-label={t('backToDashboard')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/></svg>
          {t('dashboard')}
        </button>
        <div className="svc-header__title-wrap">
          <p className="svc-header__overline">{t('serviceRequest')}</p>
          <h1 className="svc-header__title">{t('partnerHotelsTitle')}</h1>
        </div>
        <p className="svc-header__note">{t('headerNoteHotels')}</p>
      </div>

      <form className="svc-form" onSubmit={handleSubmit} noValidate>

        {/* ── Contact email ── */}
        <div className="svc-section">
          <p className="svc-section__label">{t('contactDetails')}</p>
          <div className="svc-field">
            <label className="svc-label" htmlFor="contactEmail">{t('contactEmail')}</label>
            <input
              id="contactEmail"
              type="email"
              className="svc-input"
              value={contactEmail}
              onChange={e => set('contactEmail')(e.target.value)}
              placeholder="email@example.com"
              autoComplete="email"
            />
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('destination')}</p>
          <div className="svc-row-2">
            <SearchableSelect id="country" label={t('country')} placeholder={t('searchCountry')} options={countries} value={country} onChange={handleCountryChange} loading={geoLoading} disabled={false} />
            <SearchableSelect id="city" label={t('city')} placeholder={country ? t('searchCity') : t('selectCountryFirst')} options={cities} value={city} onChange={set('city')} loading={false} disabled={!country} />
          </div>
          {country && cities.length === 0 && !geoLoading && (
            <p className="svc-inline-note" style={{ marginTop: '8px' }}>{t('noCitiesForCountry', { country })}</p>
          )}
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('dates')}</p>
          <div className="svc-row-2">
            <div className="svc-field">
              <label className="svc-label" htmlFor="checkIn">{t('checkIn')}</label>
              <input id="checkIn" type="date" className="svc-input" value={checkIn} onChange={e => set('checkIn')(e.target.value)} required />
            </div>
            <div className="svc-field">
              <label className="svc-label" htmlFor="checkOut">{t('checkOut')}</label>
              <input id="checkOut" type="date" className="svc-input" value={checkOut} onChange={e => set('checkOut')(e.target.value)} required />
            </div>
          </div>
          {n && <p className="svc-inline-note">{durationStr}</p>}
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('numberOfGuests')}</p>
          <div className="svc-steppers">
            <div className="svc-stepper">
              <span className="svc-stepper__label">{t('guests')}</span>
              <div className="svc-stepper__controls">
                <button type="button" className="svc-stepper__btn" onClick={() => set('guests')(Math.max(1,  guests - 1))} disabled={guests <= 1} aria-label={t('decrease', { label: t('guests') })}>−</button>
                <span className="svc-stepper__value">{guests}</span>
                <button type="button" className="svc-stepper__btn" onClick={() => set('guests')(Math.min(20, guests + 1))} aria-label={t('increase', { label: t('guests') })}>+</button>
              </div>
            </div>
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('roomPreferences')}</p>
          <div className="svc-checkbox-grid">
            {ROOM_PREF_IDS.map(id => (
              <label key={id} className="svc-checkbox-label">
                <input type="checkbox" className="svc-checkbox" checked={roomPrefs.includes(id)} onChange={() => togglePref(id)} />
                <span>{t(ROOM_LABEL_KEY[id])}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="svc-section">
          <div className="svc-field">
            <label className="svc-label" htmlFor="hotelSpecial">{t('specialRequirements')}</label>
            <textarea id="hotelSpecial" className="svc-textarea" rows={3} value={specialReqs} onChange={e => set('specialReqs')(e.target.value)} placeholder={t('hotelSpecialPlaceholder')} />
          </div>
        </div>

        <div className="svc-form__footer">
          <button type="submit" className="svc-btn-primary svc-btn-primary--wide">{t('reviewRequest')}</button>
        </div>

      </form>
    </div>
  )
}